import React, { useEffect, useState } from "react";
import { isEmpty } from "lodash";
import classNames from "classnames";
import { Table, TableBody, TableCell, TableFooter, TableHead, TableRow } from "@mui/material";
import { unitLabels, getOptions } from "utils/fields";
import convertUnit from "utils/convertUnit";
import { getCodeLayerLabel } from "utils/dcfExport/details";
import { exportCSV } from "utils/csv";
import InfoTooltip from "components/InfoTooltip";
import Filters from "./Filters";
import classes from "./styles.module.scss";

const sortByCategory = (a, b) => {
    if (a[0] === "first" || b[0] === "other") return -1;
    if (a[0] === "other" || b[0] === "first") return 1;
    return a[0].localeCompare(b[0]);
};

/** Aggregated geometry is summed in metric base units (m, m²) per field definitions. */
const AGGREGATE_STORAGE_LENGTH_UNIT = "m";
const AGGREGATE_STORAGE_AREA_UNIT = "m2";

const toNum = (v) => {
    const n = typeof v === "number" ? v : parseFloat(v);
    return Number.isFinite(n) ? n : 0;
};

const aggregateOutputLengthUnit = (system) => (system === "metric" ? "m" : "ft");
const aggregateOutputAreaUnit = (system) => (system === "metric" ? "m2" : "ft2");

const convertAggregateLength = (valueM, system) => {
    const out = aggregateOutputLengthUnit(system);
    if (out === AGGREGATE_STORAGE_LENGTH_UNIT) return toNum(valueM);
    return toNum(
        convertUnit({
            value: toNum(valueM),
            type: "length",
            inputUnit: AGGREGATE_STORAGE_LENGTH_UNIT,
            outputUnit: out,
        })
    );
};

const convertAggregateArea = (valueM2, system) => {
    const out = aggregateOutputAreaUnit(system);
    if (out === AGGREGATE_STORAGE_AREA_UNIT) return toNum(valueM2);
    return toNum(
        convertUnit({
            value: toNum(valueM2),
            type: "area",
            inputUnit: AGGREGATE_STORAGE_AREA_UNIT,
            outputUnit: out,
        })
    );
};

const formatAggregateLength = (valueM, system) =>
    `${convertAggregateLength(valueM, system).toFixed(2)} ${unitLabels(aggregateOutputLengthUnit(system))}`;

const formatAggregateArea = (valueM2, system, decimals = 2) =>
    `${convertAggregateArea(valueM2, system).toFixed(decimals)} ${unitLabels(aggregateOutputAreaUnit(system))}`;

const getStoreyCategoryTitle = (category) => {
    if (category === "first") return "Main";
    if (category === "second") return "2nd";
    if (category === "third") return "3rd";
    return category.charAt(0).toUpperCase() + category.slice(1);
};

const escapeCsvCell = (value) => {
    const cell = value == null ? "" : String(value);
    if (/[",\n\r]/.test(cell)) {
        return `"${cell.replace(/"/g, '""')}"`;
    }
    return cell;
};

const csvAreaValue = (valueM2, system, decimals = 2) => convertAggregateArea(valueM2, system).toFixed(decimals);
const csvLengthValue = (valueM, system) => convertAggregateLength(valueM, system).toFixed(2);
/** Plain ASCII units for CSV (avoid m²/ft² superscripts from unitLabels). */
const csvAreaUnits = (system) => aggregateOutputAreaUnit(system);
const csvLengthUnits = (system) => aggregateOutputLengthUnit(system);

export const buildAggregatedBaselineCsvData = (aggregatedData = {}, unitSystem = "imperial") => {
    const rows = [["Parameter", "Value", "Units"]];
    const add = (parameter, value, units = "") => rows.push([parameter, value, units]);

    add("Total Units", aggregatedData.totalSampleAssessments ?? 0, "");
    add(
        "Above Grade Heated Floor Area",
        csvAreaValue(aggregatedData.totalAboveGradeHeatedFloorArea ?? 0, unitSystem, 0),
        csvAreaUnits(unitSystem)
    );
    add(
        "Below Grade Heated Floor Area",
        csvAreaValue(aggregatedData.totalBelowGradeHeatedFloorArea ?? 0, unitSystem, 0),
        csvAreaUnits(unitSystem)
    );
    add(
        "Total Heated Floor Area",
        csvAreaValue(aggregatedData.totalHeatedFloorArea ?? 0, unitSystem, 0),
        csvAreaUnits(unitSystem)
    );

    Object.entries(aggregatedData.totalExposedWall || {})
        .sort(sortByCategory)
        .forEach(([category, value]) => {
            const title = getStoreyCategoryTitle(category);
            add(
                `Exposed Walls - ${title} Storey - Total Area`,
                csvAreaValue(value.area, unitSystem),
                csvAreaUnits(unitSystem)
            );
            add(
                `Exposed Walls - ${title} Storey - Total Perimeter`,
                csvLengthValue(value.perimeter, unitSystem),
                csvLengthUnits(unitSystem)
            );
        });
    add(
        "Exposed Walls - Total - Total Area",
        csvAreaValue(
            Object.values(aggregatedData.totalExposedWall || {}).reduce((acc, curr) => acc + curr.area, 0),
            unitSystem
        ),
        csvAreaUnits(unitSystem)
    );
    add(
        "Exposed Walls - Total - Total Perimeter",
        csvLengthValue(
            Object.values(aggregatedData.totalExposedWall || {}).reduce((acc, curr) => acc + curr.perimeter, 0),
            unitSystem
        ),
        csvLengthUnits(unitSystem)
    );

    Object.entries(aggregatedData.totalExposedHeader || {})
        .sort(sortByCategory)
        .forEach(([category, value]) => {
            const title = getStoreyCategoryTitle(category);
            add(
                `Exposed Above Grade Header - ${title} Storey - Total Area`,
                csvAreaValue(value.area, unitSystem),
                csvAreaUnits(unitSystem)
            );
            add(
                `Exposed Above Grade Header - ${title} Storey - Total Perimeter`,
                csvLengthValue(value.perimeter, unitSystem),
                csvLengthUnits(unitSystem)
            );
        });
    add(
        "Exposed Above Grade Header - Total - Total Area",
        csvAreaValue(
            Object.values(aggregatedData.totalExposedHeader || {}).reduce((acc, curr) => acc + curr.area, 0),
            unitSystem
        ),
        csvAreaUnits(unitSystem)
    );
    add(
        "Exposed Above Grade Header - Total - Total Perimeter",
        csvLengthValue(
            Object.values(aggregatedData.totalExposedHeader || {}).reduce((acc, curr) => acc + curr.perimeter, 0),
            unitSystem
        ),
        csvLengthUnits(unitSystem)
    );

    getWallExteriorTableRowOrder(aggregatedData.wallAreaByExterior || {}).forEach((groupKey) => {
        add(
            `Wall Area by Exterior Finish - ${getWallExteriorGroupLabel(groupKey)}`,
            csvAreaValue(aggregatedData.wallAreaByExterior?.[groupKey] || 0, unitSystem),
            csvAreaUnits(unitSystem)
        );
    });
    add(
        "Wall Area by Exterior Finish - Total",
        csvAreaValue(
            Object.values(aggregatedData.wallAreaByExterior || {}).reduce((acc, curr) => acc + curr, 0),
            unitSystem
        ),
        csvAreaUnits(unitSystem)
    );

    add(
        "Foundation Components - Exposed Foundation/Crawlspace Walls - Total Area",
        csvAreaValue(
            Object.values(aggregatedData.totalExposedFoundation || {}).reduce((acc, curr) => acc + curr.area, 0),
            unitSystem
        ),
        csvAreaUnits(unitSystem)
    );
    add(
        "Foundation Components - Exposed Foundation/Crawlspace Walls - Total Perimeter",
        csvLengthValue(
            Object.values(aggregatedData.totalExposedFoundation || {}).reduce((acc, curr) => acc + curr.perimeter, 0),
            unitSystem
        ),
        csvLengthUnits(unitSystem)
    );
    add(
        "Foundation Components - Exposed Foundation Headers - Total Area",
        csvAreaValue(
            Object.values(aggregatedData.totalExposedFoundationHeader || {}).reduce((acc, curr) => acc + curr.area, 0),
            unitSystem
        ),
        csvAreaUnits(unitSystem)
    );
    add(
        "Foundation Components - Exposed Foundation Headers - Total Perimeter",
        csvLengthValue(
            Object.values(aggregatedData.totalExposedFoundationHeader || {}).reduce(
                (acc, curr) => acc + curr.perimeter,
                0
            ),
            unitSystem
        ),
        csvLengthUnits(unitSystem)
    );
    add(
        "Foundation Components - Below Grade Slab - Total Area",
        csvAreaValue(aggregatedData.totalBelowGradeSlabArea ?? 0, unitSystem),
        csvAreaUnits(unitSystem)
    );
    add("Foundation Components - Below Grade Slab - Total Perimeter", "", "N/A");
    add(
        "Foundation Components - Slab-on-grade - Total Area",
        csvAreaValue(aggregatedData.totalOnGradeSlabArea ?? 0, unitSystem),
        csvAreaUnits(unitSystem)
    );
    add("Foundation Components - Slab-on-grade - Total Perimeter", "", "N/A");

    add(
        "Other Above Grade Components - Attics - Total Area",
        csvAreaValue(
            Object.values(aggregatedData.attic || {}).reduce((acc, curr) => acc + curr.area, 0),
            unitSystem
        ),
        csvAreaUnits(unitSystem)
    );
    add(
        "Other Above Grade Components - Exposed Floors - Total Area",
        csvAreaValue(
            Object.values(aggregatedData.exposedFloors || {}).reduce((acc, curr) => acc + curr.area, 0),
            unitSystem
        ),
        csvAreaUnits(unitSystem)
    );
    add(
        "Other Above Grade Components - Sloped Ceilings - Total Area",
        csvAreaValue(
            Object.values(aggregatedData.slopedCeilings || {}).reduce((acc, curr) => acc + curr.area, 0),
            unitSystem
        ),
        csvAreaUnits(unitSystem)
    );
    add(
        "Other Above Grade Components - Flat Roofs - Total Area",
        csvAreaValue(
            Object.values(aggregatedData.flatRoofs || {}).reduce((acc, curr) => acc + curr.area, 0),
            unitSystem
        ),
        csvAreaUnits(unitSystem)
    );

    add("Fenestration - Doors Count", aggregatedData.componentsCount?.Doors ?? 0, "");
    add(
        "Fenestration - Windows (excl. windows in doors) Count",
        aggregatedData.componentsCount?.["Windows (excl. windows in doors)"] ?? 0,
        ""
    );
    add(
        "Fenestration - Total Window Area (excl. windows in doors)",
        csvAreaValue(aggregatedData.totalWindowArea ?? 0, unitSystem),
        csvAreaUnits(unitSystem)
    );

    add("Blower Test Results - Highest", aggregatedData.highestAch50Pa ?? 0, "ACH");
    add("Blower Test Results - Average", aggregatedData.averageAch50Pa ?? 0, "ACH");
    add("Blower Test Results - Lowest", aggregatedData.lowestAch50Pa ?? 0, "ACH");
    add("Blower Test Results - Total No. of Sample Assessments", aggregatedData.totalSampleAssessments ?? 0, "");

    ["Electric", "Natural Gas", "Oil", "Propane", "Wood"].forEach((fuel) => {
        add(`Primary Heating by Fuel Type - ${fuel}`, aggregatedData.primaryHeatingCountsByFuelType?.[fuel] ?? 0, "");
    });

    ["Electric", "Natural Gas", "Oil", "Propane", "Wood", "Solar"].forEach((fuel) => {
        add(`Hot Water by Fuel Type - ${fuel}`, aggregatedData.dhwSystemCountsByFuelType?.[fuel] ?? 0, "");
    });
    add("Hot Water by Fuel Type - Total Tank Volume", (aggregatedData.totalDHWVolume || 0).toFixed(1), "L");

    ["Furnaces", "Boilers", "DHW systems", "DWHR systems", "AC's", "ASHP's", "WSHP/GSHP's"].forEach((equipment) => {
        add(`Equipment Type Counts - ${equipment}`, aggregatedData.componentsCount?.[equipment] ?? 0, "");
    });

    add("Ventilation - HRVs / ERVs", aggregatedData.componentsCount?.["HRVs / ERVs"] ?? 0, "");
    add("Ventilation - Bathroom Fans", aggregatedData.componentsCount?.["Bathroom Fans"] ?? 0, "");

    add(
        "Generation - Solar PV Module Area",
        csvAreaValue(aggregatedData.solarPV?.totalArrayArea ?? 0, unitSystem),
        csvAreaUnits(unitSystem)
    );
    add("Generation - Solar PV Array Capacity", (aggregatedData.solarPV?.totalArrayCapacity ?? 0).toFixed(2), "kW");

    return rows.map((row) => row.map(escapeCsvCell));
};

const WALL_EXTERIOR_GROUP_KEYS = {
    USER_SPECIFIED: "UserSpecified",
    USER_DEFINED: "UserDefined",
};

const CS_WALL_EXTERIOR_OPTIONS = getOptions({ fieldKey: "csWallExterior" });

const getModelAggregatedMultiplier = (modelId, communityModelCountsMap, applyModelResultMultiplier) => {
    if (!applyModelResultMultiplier) {
        return 1;
    }
    const total = Object.entries(communityModelCountsMap)
        .filter(([key]) => key.startsWith(`${modelId}_`))
        .reduce((sum, [, count]) => sum + Math.max(0, Number(count) || 0), 0);
    return total > 0 ? total : 1;
};

const getWallExteriorGroupKey = (codeRef, codes = {}) => {
    if (codeRef === "UserSpecified") {
        return WALL_EXTERIOR_GROUP_KEYS.USER_SPECIFIED;
    }
    if (codeRef.includes("-U-")) {
        return WALL_EXTERIOR_GROUP_KEYS.USER_DEFINED;
    }
    const exteriorId = codes[codeRef]?.layers?.exterior?.id;
    if (exteriorId !== undefined && exteriorId !== null) {
        return `standard_${exteriorId}`;
    }
    return null;
};

const getWallExteriorGroupLabel = (groupKey) => {
    if (groupKey === WALL_EXTERIOR_GROUP_KEYS.USER_SPECIFIED) {
        return "User Specified";
    }
    if (groupKey === WALL_EXTERIOR_GROUP_KEYS.USER_DEFINED) {
        return "User Defined";
    }
    if (groupKey?.startsWith("standard_")) {
        const exteriorId = Number(groupKey.replace("standard_", ""));
        return getCodeLayerLabel("csWallExterior", exteriorId) || groupKey;
    }
    return groupKey;
};

const getPvSystemAreaM2 = (pvSystem = {}) => {
    const { array: { area = 0, count = 0, width = 0, height = 0 } = {} } = pvSystem;
    const storedArea = toNum(area);
    if (storedArea > 0) {
        return storedArea;
    }
    return toNum(count) * toNum(width) * toNum(height);
};

const getPvSystemCapacityKw = (pvSystem = {}) => {
    const { array: { capacity = 0, capacity_u: capacityUnit = "kW" } = {} } = pvSystem;
    const cap = toNum(capacity);
    return capacityUnit === "W" ? cap / 1000 : cap;
};

const BATHROOM_VENTILATOR_TYPE_ID = 3;

const countBathroomVentilators = (ventilators = {}) =>
    Object.values(ventilators).filter(({ type: { id } = {} }) => id === BATHROOM_VENTILATOR_TYPE_ID).length;

const countHrvSystems = (ventilation = {}) => {
    const { wholeHouseSystems = {}, supplementalSystems = {} } = ventilation;
    let count = 0;
    if (!isEmpty(wholeHouseSystems.hrv)) {
        count += 1;
    }
    if (!isEmpty(supplementalSystems.hrv)) {
        count += 1;
    }
    return count;
};

const getWallExteriorTableRowOrder = (wallAreaByExterior = {}) => {
    const standardKeys = CS_WALL_EXTERIOR_OPTIONS.map(({ value: { id } = {} }) => `standard_${id}`).filter(
        (key) => wallAreaByExterior[key] > 0
    );

    const specialKeys = [WALL_EXTERIOR_GROUP_KEYS.USER_SPECIFIED, WALL_EXTERIOR_GROUP_KEYS.USER_DEFINED].filter(
        (key) => wallAreaByExterior[key] > 0
    );

    return [...standardKeys, ...specialKeys];
};

const fuelTypeMap = {
    0: "Electric",
    1: "Natural Gas",
    2: "Oil",
    3: "Propane",
    4: "Wood",
    5: "Wood",
    6: "Wood",
    7: "Wood",
    8: "Solar",
};

// Total Units
// Total Square Footage
// Total Main Wall Area
// Siding Wall Area
// Brick Wall Area
// Total Attic Area
// Total Slope Ceiling Area
// Flat Roof Area
// Total Exposed Foundation Header Area (can include crawlspace headers)
// Total Exposed Floor Areas
// Total Exposed Foundation Area
// Total Exposed Crawlspace Wall Area
// Number of Windows (not including windows in doors)
// Total Window(s) Area
// Number of Doors
// Number of Furnaces
// Number of DHW systems and tank volume
// Number of AC’s
// Number of ASHP’s
// Number of ERV’s
// Number of Bathroom Fans
// Solar PV Area + kW
// Number of Drain Water

const getAggregatedData = (
    modelsData = {},
    selectedModels = [],
    { communityModelCountsMap = {}, applyModelResultMultiplier = false } = {}
) => {
    const exposedWalls = {};
    const totalExposedWall = {};
    const totalExposedHeader = {};
    const totalExposedFoundation = {};
    const totalExposedFoundationHeader = {};
    const wallAreaByExterior = {};
    let totalOnGradeSlabArea = 0;
    let totalBelowGradeSlabArea = 0;
    let totalWindowArea = 0;
    const attic = {};
    const slopedCeilings = {};
    const flatRoofs = {};
    const exposedFloors = {};
    let totalAboveGradeHeatedFloorArea = 0;
    let totalBelowGradeHeatedFloorArea = 0;
    const componentsCount = {
        Doors: 0,
        "Windows (excl. windows in doors)": 0,
        Furnaces: 0,
        Boilers: 0,
        "DHW systems": 0,
        "DWHR systems": 0,
        "AC's": 0,
        "ASHP's": 0,
        "WSHP/GSHP's": 0,
        "HRVs / ERVs": 0,
        "Bathroom Fans": 0,
    };
    const primaryHeatingCountsByFuelType = {
        Electric: 0,
        "Natural Gas": 0,
        Oil: 0,
        Propane: 0,
        Wood: 0,
    };
    const dhwSystemCountsByFuelType = {
        Electric: 0,
        "Natural Gas": 0,
        Oil: 0,
        Propane: 0,
        Wood: 0,
        Solar: 0,
    };
    var totalDHWVolume = 0;
    var highestAch50Pa = 0;
    var lowestAch50Pa = 0;
    var averageAch50PaSum = 0;
    var averageAch50PaWeight = 0;
    let totalSampleAssessments = 0;
    const solarPV = {
        totalArrayArea: 0,

        totalArrayCapacity: 0,
    };

    Object.entries(modelsData.models || {})
        .filter(([modelId]) => selectedModels.includes(modelId))
        .forEach(([modelId, modelData]) => {
            const modelMultiplier = getModelAggregatedMultiplier(
                modelId,
                communityModelCountsMap,
                applyModelResultMultiplier
            );
            totalSampleAssessments += modelMultiplier;

            const {
                components = {},
                heatingCooling = {},
                domesticHotWater = {},
                airTightness = {},
                generation = {},
                ventilation = {},
                dimensions = {},
                codes = {},
            } = modelData;
            const { wall = {}, ceiling = {}, basement = {}, crawlspace = {}, slab = {}, expFloor = {} } = components;
            const {
                aboveGradeIntFloorArea: { total: aboveGradeFloorArea = 0 } = {},
                belowGradeIntFloorArea: { total: belowGradeFloorArea = 0 } = {},
            } = dimensions;

            totalAboveGradeHeatedFloorArea += toNum(aboveGradeFloorArea) * modelMultiplier;
            totalBelowGradeHeatedFloorArea += toNum(belowGradeFloorArea) * modelMultiplier;

            Object.entries(wall).forEach(([wallKey, wallData]) => {
                const {
                    category,
                    subcomponents = {},
                    measurements: { perimeter = 0, height = 0 } = {},
                    wallInsType: { codeRef = "" } = {},
                } = wallData;

                const wallArea = perimeter * height * modelMultiplier;
                const wallPerimeter = perimeter * modelMultiplier;
                const exteriorGroupKey = getWallExteriorGroupKey(codeRef, codes);

                if (exteriorGroupKey) {
                    wallAreaByExterior[exteriorGroupKey] = (wallAreaByExterior[exteriorGroupKey] || 0) + wallArea;
                }

                if (!Object.keys(exposedWalls).includes(category)) {
                    exposedWalls[category] = {};
                    totalExposedWall[category] = {
                        area: 0,
                        perimeter: 0,
                    };
                    totalExposedHeader[category] = {
                        area: 0,
                        perimeter: 0,
                    };
                }

                const { door = {}, window = {}, floorHeader = {} } = subcomponents;

                exposedWalls[category][modelId] = {
                    ...exposedWalls[category][modelId],
                    [wallKey]: {
                        area: wallArea,
                        perimeter: wallPerimeter,
                        subcomponents,
                    },
                };
                totalExposedWall[category].area += wallArea;
                totalExposedWall[category].perimeter += wallPerimeter;

                Object.entries(floorHeader).forEach(([floorHeaderKey, floorHeaderData]) => {
                    const {
                        measurements: { height: floorHeaderHeight = 0, perimeter: floorHeaderPerimeter = 0 } = {},
                    } = floorHeaderData;
                    totalExposedHeader[category].area += floorHeaderHeight * floorHeaderPerimeter * modelMultiplier;
                    totalExposedHeader[category].perimeter += floorHeaderPerimeter * modelMultiplier;
                });

                Object.entries(door).forEach(() => {
                    componentsCount["Doors"] += modelMultiplier;
                });

                Object.entries(window).forEach(([windowKey, windowData]) => {
                    const { numIdentical = 1, measurements: { width = 0, height = 0 } = {} } = windowData;
                    componentsCount["Windows (excl. windows in doors)"] += numIdentical * modelMultiplier;

                    totalWindowArea += ((numIdentical * (width * height)) / 1000000) * modelMultiplier; // Convert mm2 to m2
                });
            });

            Object.entries({ ...basement, ...crawlspace }).forEach(([basementKey, basementData]) => {
                const {
                    label = "",
                    wall: { measurements: { height = 0 } = {} } = {},
                    floor: {
                        measurements: { isRectangular = false, perimeter = 0, width = 0, length = 0, area = 0 } = {},
                    } = {},
                    subcomponents: { floorHeader = {}, door = {}, window = {} } = {},
                } = basementData;

                const perimeterToUse = isRectangular ? 2 * (width + length) : perimeter;
                const slabAreaToUse = isRectangular ? width * length : area;

                totalExposedFoundation[basementKey] = {
                    area: (totalExposedFoundation[basementKey]?.area || 0) + perimeterToUse * height * modelMultiplier,
                    perimeter: (totalExposedFoundation[basementKey]?.perimeter || 0) + perimeterToUse * modelMultiplier,
                    label,
                };

                totalBelowGradeSlabArea += slabAreaToUse * modelMultiplier;

                Object.entries(floorHeader).forEach(([floorHeaderKey, floorHeaderData]) => {
                    const {
                        label: floorHeaderLabel = "",
                        measurements: { height: floorHeaderHeight = 0, perimeter: floorHeaderPerimeter = 0 } = {},
                    } = floorHeaderData;
                    totalExposedFoundationHeader[basementKey] = {
                        area:
                            (totalExposedFoundationHeader[basementKey]?.area || 0) +
                            floorHeaderHeight * floorHeaderPerimeter * modelMultiplier,
                        perimeter:
                            (totalExposedFoundationHeader[basementKey]?.perimeter || 0) +
                            floorHeaderPerimeter * modelMultiplier,
                        label: floorHeaderLabel || label,
                    };
                });

                Object.entries(door).forEach(() => {
                    componentsCount["Doors"] += modelMultiplier;
                });

                Object.entries(window).forEach(([windowKey, windowData]) => {
                    const { numIdentical = 1, measurements: { width = 0, height = 0 } = {} } = windowData;
                    componentsCount["Windows (excl. windows in doors)"] += numIdentical * modelMultiplier;

                    totalWindowArea += ((numIdentical * (width * height)) / 1000000) * modelMultiplier; // Convert mm2 to m2
                });
            });

            Object.entries(slab).forEach(([slabKey, slabData]) => {
                const {
                    floor: { measurements: { isRectangular = false, width = 0, length = 0, area = 0 } = {} } = {},
                } = slabData;
                totalOnGradeSlabArea += (isRectangular ? width * length : area) * modelMultiplier;
            });

            Object.entries(ceiling).forEach(([ceilingKey, ceilingData]) => {
                const {
                    constructionType: { id = 0 } = {},
                    label = "",
                    measurements: { area = 0 },
                } = ceilingData;
                const scaledArea = area * modelMultiplier;
                // Attic
                if (id === 0 || id === 1) {
                    attic[ceilingKey] = {
                        area: (attic[ceilingKey]?.area || 0) + scaledArea,
                        label,
                    };
                }
                if (id === 2 || id === 4) {
                    slopedCeilings[ceilingKey] = {
                        area: (slopedCeilings[ceilingKey]?.area || 0) + scaledArea,
                        label,
                    };
                }
                if (id === 3) {
                    flatRoofs[ceilingKey] = {
                        area: (flatRoofs[ceilingKey]?.area || 0) + scaledArea,
                        label,
                    };
                }
            });

            Object.entries(expFloor).forEach(([expFloorKey, expFloorData]) => {
                const { label = "", measurements: { area = 0 } = {} } = expFloorData;
                exposedFloors[expFloorKey] = {
                    area: (exposedFloors[expFloorKey]?.area || 0) + area * modelMultiplier,
                    label,
                };
            });

            const { heating: { system: heatingSystem = {} } = {}, cooling: { system: coolingSystem = {} } = {} } =
                heatingCooling;

            if ("furnace" in heatingSystem) {
                componentsCount["Furnaces"] += modelMultiplier;
                primaryHeatingCountsByFuelType[fuelTypeMap[heatingSystem.furnace.energySource.id]] += modelMultiplier;
            }

            if ("boiler" in heatingSystem) {
                componentsCount["Boilers"] += modelMultiplier;
                primaryHeatingCountsByFuelType[fuelTypeMap[heatingSystem.boiler.energySource.id]] += modelMultiplier;
            }

            if ("airHeatPump" in coolingSystem) {
                componentsCount["ASHP's"] += modelMultiplier;
            }

            if ("waterHeatPump" in coolingSystem || "groundHeatPump" in coolingSystem) {
                componentsCount["WSHP/GSHP's"] += modelMultiplier;
            }

            if ("airConditioning" in coolingSystem) {
                componentsCount["AC's"] += modelMultiplier;
            }

            const { primarySystem = {}, secondarySystem = {} } = domesticHotWater;

            if (!isEmpty(primarySystem)) {
                componentsCount["DHW systems"] += modelMultiplier;
                totalDHWVolume += (parseFloat(primarySystem.tankVolume.value) || 0) * modelMultiplier;
                dhwSystemCountsByFuelType[fuelTypeMap[primarySystem.energySource.id]] += modelMultiplier;
                if (!isEmpty(primarySystem.drainWaterHeatRecovery)) {
                    componentsCount["DWHR systems"] += modelMultiplier;
                }
            }
            if (!isEmpty(secondarySystem)) {
                componentsCount["DHW systems"] += modelMultiplier;
                totalDHWVolume += (parseFloat(secondarySystem.tankVolume.value) || 0) * modelMultiplier;
                dhwSystemCountsByFuelType[fuelTypeMap[secondarySystem.energySource.id]] += modelMultiplier;
                if (!isEmpty(secondarySystem.drainWaterHeatRecovery)) {
                    componentsCount["DWHR systems"] += modelMultiplier;
                }
            }

            const { pvSystems = {} } = generation;
            Object.values(pvSystems).forEach((pvSystem) => {
                solarPV.totalArrayArea += getPvSystemAreaM2(pvSystem) * modelMultiplier;
                solarPV.totalArrayCapacity += getPvSystemCapacityKw(pvSystem) * modelMultiplier;
            });

            componentsCount["HRVs / ERVs"] += countHrvSystems(ventilation) * modelMultiplier;

            const { wholeHouseSystems = {}, supplementalSystems = {} } = ventilation;
            componentsCount["Bathroom Fans"] +=
                (countBathroomVentilators(wholeHouseSystems.ventilators) +
                    countBathroomVentilators(supplementalSystems.ventilators)) *
                modelMultiplier;

            const { blowerTest: { airChangeRate50Pa = 0 } = {} } = airTightness;

            if (airChangeRate50Pa > highestAch50Pa) {
                highestAch50Pa = airChangeRate50Pa;
            }
            if (airChangeRate50Pa < lowestAch50Pa || lowestAch50Pa === 0) {
                lowestAch50Pa = airChangeRate50Pa;
            }
            averageAch50PaSum += airChangeRate50Pa * modelMultiplier;
            averageAch50PaWeight += modelMultiplier;
        });

    const averageAch50Pa =
        averageAch50PaWeight > 0 ? parseFloat((averageAch50PaSum / averageAch50PaWeight).toFixed(2)) : 0;

    return {
        exposedWalls,
        totalExposedWall,
        totalExposedHeader,
        totalExposedFoundation,
        totalExposedFoundationHeader,
        wallAreaByExterior,
        totalOnGradeSlabArea,
        totalBelowGradeSlabArea,
        totalWindowArea,
        attic,
        slopedCeilings,
        flatRoofs,
        exposedFloors,
        totalAboveGradeHeatedFloorArea,
        totalBelowGradeHeatedFloorArea,
        totalHeatedFloorArea: totalAboveGradeHeatedFloorArea + totalBelowGradeHeatedFloorArea,
        componentsCount,
        totalDHWVolume,
        highestAch50Pa,
        lowestAch50Pa,
        averageAch50Pa,
        totalSampleAssessments,
        primaryHeatingCountsByFuelType,
        dhwSystemCountsByFuelType,
        solarPV,
    };
};

const AggregatedData = ({
    allModels = [],
    modelsData = {},
    communityModelCountsMap = {},
    applyModelResultMultiplier = false,
    setApplyModelResultMultiplier = () => {},
}) => {
    const [aggregatedData, setAggregatedData] = useState({});
    const [selectedModels, setSelectedModels] = useState(allModels.map((model) => model.value));
    const [aggregatedUnitSystem, setAggregatedUnitSystem] = useState("imperial");

    const handleExportCSV = () => {
        const csvData = buildAggregatedBaselineCsvData(aggregatedData, aggregatedUnitSystem);
        exportCSV(csvData, "community_aggregated_baseline_data.csv");
    };

    useEffect(() => {
        const aggregatedData = getAggregatedData(modelsData, selectedModels, {
            communityModelCountsMap,
            applyModelResultMultiplier,
        });
        setAggregatedData(aggregatedData);
    }, [modelsData, selectedModels, communityModelCountsMap, applyModelResultMultiplier]);

    return (
        <div className={classes.aggregatedDataContainer}>
            <h2 className={classes.aggregatedDataTitle}>Aggregated Baseline Data</h2>
            <Filters
                allModels={allModels}
                selectedModels={selectedModels}
                setSelectedModels={setSelectedModels}
                aggregatedUnitSystem={aggregatedUnitSystem}
                setAggregatedUnitSystem={setAggregatedUnitSystem}
                applyModelResultMultiplier={applyModelResultMultiplier}
                setApplyModelResultMultiplier={setApplyModelResultMultiplier}
                handleExportCSV={handleExportCSV}
            />
            <div className={classes.summaryRow}>
                <div className={classes.summaryItem}>
                    <span className={classes.summaryLabel}>Total Units</span>
                    <span className={classes.summaryValue}>{aggregatedData.totalSampleAssessments ?? 0}</span>
                </div>
                <div className={classes.summaryItem}>
                    <span className={classes.summaryLabel}>Above Grade Heated Floor Area</span>
                    <span className={classes.summaryValue}>
                        {formatAggregateArea(
                            aggregatedData.totalAboveGradeHeatedFloorArea ?? 0,
                            aggregatedUnitSystem,
                            0
                        )}
                    </span>
                </div>
                <div className={classes.summaryItem}>
                    <span className={classes.summaryLabel}>Below Grade Heated Floor Area</span>
                    <span className={classes.summaryValue}>
                        {formatAggregateArea(
                            aggregatedData.totalBelowGradeHeatedFloorArea ?? 0,
                            aggregatedUnitSystem,
                            0
                        )}
                    </span>
                </div>
                <div className={classes.summaryItem}>
                    <span className={classes.summaryLabel}>Total Heated Floor Area</span>
                    <span className={classes.summaryValue}>
                        {formatAggregateArea(aggregatedData.totalHeatedFloorArea ?? 0, aggregatedUnitSystem, 0)}
                    </span>
                </div>
            </div>
            <div className={classes.tablesContainer}>
                <Table className={classNames(classes.tableContainer)}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Exposed Walls</TableCell>
                            <TableCell className={classes.headerCellWithTooltip}>
                                Total Area
                                <InfoTooltip info="Including floor header areas" className={classes.infoTooltip} />
                            </TableCell>
                            <TableCell>Total Perimeter</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {Object.entries(aggregatedData.totalExposedWall || {})
                            .sort(sortByCategory)
                            .map(([category, value]) => {
                                const title = getStoreyCategoryTitle(category);

                                return (
                                    <TableRow key={category}>
                                        <TableCell>{title} Storey</TableCell>
                                        <TableCell>{formatAggregateArea(value.area, aggregatedUnitSystem)}</TableCell>
                                        <TableCell>
                                            {formatAggregateLength(value.perimeter, aggregatedUnitSystem)}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TableCell>Total</TableCell>
                            <TableCell>
                                {formatAggregateArea(
                                    Object.values(aggregatedData.totalExposedWall || {}).reduce(
                                        (acc, curr) => acc + curr.area,
                                        0
                                    ),
                                    aggregatedUnitSystem
                                )}
                            </TableCell>
                            <TableCell>
                                {formatAggregateLength(
                                    Object.values(aggregatedData.totalExposedWall || {}).reduce(
                                        (acc, curr) => acc + curr.perimeter,
                                        0
                                    ),
                                    aggregatedUnitSystem
                                )}
                            </TableCell>
                        </TableRow>
                    </TableFooter>
                </Table>

                {/* Exposed Header Components */}
                <Table className={classNames(classes.tableContainer)}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Exposed Above Grade Header</TableCell>
                            <TableCell>Total Area </TableCell>
                            <TableCell>Total Perimeter</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {Object.entries(aggregatedData.totalExposedHeader || {})
                            .sort(sortByCategory)
                            .map(([category, value]) => {
                                const title = getStoreyCategoryTitle(category);
                                return (
                                    <TableRow key={category}>
                                        <TableCell>{title} Storey</TableCell>
                                        <TableCell>{formatAggregateArea(value.area, aggregatedUnitSystem)}</TableCell>
                                        <TableCell>
                                            {formatAggregateLength(value.perimeter, aggregatedUnitSystem)}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TableCell>Total</TableCell>
                            <TableCell>
                                {formatAggregateArea(
                                    Object.values(aggregatedData.totalExposedHeader || {}).reduce(
                                        (acc, curr) => acc + curr.area,
                                        0
                                    ),
                                    aggregatedUnitSystem
                                )}
                            </TableCell>
                            <TableCell>
                                {formatAggregateLength(
                                    Object.values(aggregatedData.totalExposedHeader || {}).reduce(
                                        (acc, curr) => acc + curr.perimeter,
                                        0
                                    ),
                                    aggregatedUnitSystem
                                )}
                            </TableCell>
                        </TableRow>
                    </TableFooter>
                </Table>

                {/* Wall Area by Exterior Finish */}
                <Table className={classNames(classes.tableContainer, classes.twoColumnTable)}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Wall Area by Exterior Finish</TableCell>
                            <TableCell>Total Area</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {getWallExteriorTableRowOrder(aggregatedData.wallAreaByExterior || {}).map((groupKey) => (
                            <TableRow key={groupKey}>
                                <TableCell>{getWallExteriorGroupLabel(groupKey)}</TableCell>
                                <TableCell>
                                    {formatAggregateArea(
                                        aggregatedData.wallAreaByExterior?.[groupKey] || 0,
                                        aggregatedUnitSystem
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TableCell>Total</TableCell>
                            <TableCell>
                                {formatAggregateArea(
                                    Object.values(aggregatedData.wallAreaByExterior || {}).reduce(
                                        (acc, curr) => acc + curr,
                                        0
                                    ),
                                    aggregatedUnitSystem
                                )}
                            </TableCell>
                        </TableRow>
                    </TableFooter>
                </Table>

                {/* Foundation Components */}
                <Table className={classNames(classes.tableContainer)}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Foundation Components</TableCell>
                            <TableCell>Total Area</TableCell>
                            <TableCell>Total Perimeter</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell>Exposed Foundation/Crawlspace Walls</TableCell>
                            <TableCell>
                                {formatAggregateArea(
                                    Object.values(aggregatedData.totalExposedFoundation || {}).reduce(
                                        (acc, curr) => acc + curr.area,
                                        0
                                    ),
                                    aggregatedUnitSystem
                                )}
                            </TableCell>
                            <TableCell>
                                {formatAggregateLength(
                                    Object.values(aggregatedData.totalExposedFoundation || {}).reduce(
                                        (acc, curr) => acc + curr.perimeter,
                                        0
                                    ),
                                    aggregatedUnitSystem
                                )}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Exposed Foundation Headers</TableCell>
                            <TableCell>
                                {formatAggregateArea(
                                    Object.values(aggregatedData.totalExposedFoundationHeader || {}).reduce(
                                        (acc, curr) => acc + curr.area,
                                        0
                                    ),
                                    aggregatedUnitSystem
                                )}
                            </TableCell>
                            <TableCell>
                                {formatAggregateLength(
                                    Object.values(aggregatedData.totalExposedFoundationHeader || {}).reduce(
                                        (acc, curr) => acc + curr.perimeter,
                                        0
                                    ),
                                    aggregatedUnitSystem
                                )}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Exposed Foundation Walls & Headers</TableCell>
                            <TableCell>
                                {formatAggregateArea(
                                    Object.values(aggregatedData.totalExposedFoundationHeader || {}).reduce(
                                        (acc, curr) => acc + curr.area,
                                        0
                                    ) +
                                        Object.values(aggregatedData.totalExposedFoundation || {}).reduce(
                                            (acc, curr) => acc + curr.area,
                                            0
                                        ),
                                    aggregatedUnitSystem
                                )}
                            </TableCell>
                            <TableCell>
                                {formatAggregateLength(
                                    Object.values(aggregatedData.totalExposedFoundation || {}).reduce(
                                        (acc, curr) => acc + curr.perimeter,
                                        0
                                    ),
                                    aggregatedUnitSystem
                                )}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Below Grade Slab</TableCell>
                            <TableCell>
                                {formatAggregateArea(aggregatedData.totalBelowGradeSlabArea, aggregatedUnitSystem)}
                            </TableCell>
                            <TableCell>N/A</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Slab-on-grade</TableCell>
                            <TableCell>
                                {formatAggregateArea(aggregatedData.totalOnGradeSlabArea, aggregatedUnitSystem)}
                            </TableCell>
                            <TableCell>N/A</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>

                {/* Other Above Grade Components */}
                <Table className={classNames(classes.tableContainer)}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Other Above Grade Components</TableCell>
                            <TableCell>Total Area</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell>Attics</TableCell>
                            <TableCell>
                                {formatAggregateArea(
                                    Object.values(aggregatedData.attic || {}).reduce((acc, curr) => acc + curr.area, 0),
                                    aggregatedUnitSystem
                                )}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Exposed Floors</TableCell>
                            <TableCell>
                                {formatAggregateArea(
                                    Object.values(aggregatedData.exposedFloors || {}).reduce(
                                        (acc, curr) => acc + curr.area,
                                        0
                                    ),
                                    aggregatedUnitSystem
                                )}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Sloped Ceilings</TableCell>
                            <TableCell>
                                {formatAggregateArea(
                                    Object.values(aggregatedData.slopedCeilings || {}).reduce(
                                        (acc, curr) => acc + curr.area,
                                        0
                                    ),
                                    aggregatedUnitSystem
                                )}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Flat Roofs</TableCell>
                            <TableCell>
                                {formatAggregateArea(
                                    Object.values(aggregatedData.flatRoofs || {}).reduce(
                                        (acc, curr) => acc + curr.area,
                                        0
                                    ),
                                    aggregatedUnitSystem
                                )}
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>

                {/* Envelope Components by Count */}
                <Table className={classNames(classes.tableContainer)}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Fenestration</TableCell>
                            <TableCell>Total</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {["Doors", "Windows (excl. windows in doors)"].map((component) => (
                            <TableRow key={component}>
                                <TableCell>{component} Count</TableCell>
                                <TableCell>{aggregatedData.componentsCount?.[component] || 0}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TableCell>Total Window Area (excl. windows in doors)</TableCell>
                            <TableCell>
                                {formatAggregateArea(aggregatedData?.totalWindowArea || 0, aggregatedUnitSystem)}
                            </TableCell>
                        </TableRow>
                    </TableFooter>
                </Table>

                {/* Blower Test Results */}
                <Table className={classNames(classes.tableContainer, classes.twoColumnTable)}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Blower Test Results</TableCell>
                            <TableCell>ACH@50Pa</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell>Highest</TableCell>
                            <TableCell>{aggregatedData.highestAch50Pa} ACH</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Average</TableCell>
                            <TableCell>{aggregatedData.averageAch50Pa} ACH</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Lowest</TableCell>
                            <TableCell>{aggregatedData.lowestAch50Pa} ACH</TableCell>
                        </TableRow>
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TableCell>Total No. of Sample Assessments</TableCell>
                            <TableCell>{aggregatedData.totalSampleAssessments}</TableCell>
                        </TableRow>
                    </TableFooter>
                </Table>

                {/* Primary Heating by Fuel Type */}
                <Table className={classNames(classes.tableContainer, classes.twoColumnTable)}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Primary Heating by Fuel Type</TableCell>
                            <TableCell>Count</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {["Electric", "Natural Gas", "Oil", "Propane", "Wood"].map((component) => (
                            <TableRow key={component}>
                                <TableCell>{component}</TableCell>
                                <TableCell>{aggregatedData.primaryHeatingCountsByFuelType?.[component] || 0}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                {/* Hot Water by Fuel Type */}
                <Table className={classNames(classes.tableContainer, classes.twoColumnTable)}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Hot Water by Fuel Type</TableCell>
                            <TableCell>Count</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {["Electric", "Natural Gas", "Oil", "Propane", "Wood", "Solar"].map((component) => (
                            <TableRow key={component}>
                                <TableCell>{component}</TableCell>
                                <TableCell>{aggregatedData.dhwSystemCountsByFuelType?.[component] || 0}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TableCell>Total Tank Volume</TableCell>
                            <TableCell>{(aggregatedData.totalDHWVolume || 0).toFixed(1)} L</TableCell>
                        </TableRow>
                    </TableFooter>
                </Table>

                {/* Equipment by Count */}
                <Table className={classNames(classes.tableContainer)}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Equipment Type Counts</TableCell>
                            <TableCell>Count</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {["Furnaces", "Boilers", "DHW systems", "DWHR systems", "AC's", "ASHP's", "WSHP/GSHP's"].map(
                            (component) => (
                                <TableRow key={component}>
                                    <TableCell>{component}</TableCell>
                                    <TableCell>{aggregatedData.componentsCount?.[component] || 0}</TableCell>
                                </TableRow>
                            )
                        )}
                    </TableBody>
                </Table>

                {/* Ventilation */}
                <Table className={classNames(classes.tableContainer)}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Equipment Type Counts</TableCell>
                            <TableCell>Count</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {["HRVs / ERVs", "Bathroom Fans"].map((component) => (
                            <TableRow key={component}>
                                <TableCell>{component}</TableCell>
                                <TableCell>{aggregatedData.componentsCount?.[component] || 0}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

                {/* Generation */}
                <Table className={classNames(classes.tableContainer, classes.twoColumnTable)}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Generation</TableCell>
                            <TableCell>Total</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell>Solar PV Module Area</TableCell>
                            <TableCell>
                                {formatAggregateArea(aggregatedData.solarPV?.totalArrayArea || 0, aggregatedUnitSystem)}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Solar PV Array Capacity</TableCell>
                            <TableCell>{(aggregatedData.solarPV?.totalArrayCapacity || 0).toFixed(2)} kW</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </div>
        </div>
    );
};

export default AggregatedData;
